import i18n from "i18next";
import {
	ReclamationStateValues,
	getReclamationStateLabels,
} from "src/api/generated/erp/db/types/enums/reclamationState.ts";
import { ReclamationView } from "src/api/generated/erp/db/types/tables/reclamationView.ts";
import { ReclamationsDataGridApi } from "src/api/generated/erp/reclamations/api/reclamationsDataGridApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateTimeColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SingleReclamationView } from "src/components/views/erp/reclamation/singleReclamationView/SingleReclamationView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ReclamationsOfCustomerDataGridProps {
	customerId: number;
}

export const ReclamationsOfCustomerDataGrid = ({ customerId }: ReclamationsOfCustomerDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ServerSideDataGridModel
			gridId={"AA9E9564A4A64BC5"}
			fetchData={(params) => ReclamationsDataGridApi.getData(params)}
			getDataModelResult={(response) => response}
			initialParams={{
				customerId,
				states: [...ReclamationStateValues],
				searchQuery: "",
				categoryIds: [],
			}}
			getRowId={(row) => row.reclamationId}
			render={({ dataGridProps }) => (
				<AavoDataGrid<ReclamationView>
					disableMultipleRowSelection
					columns={[
						integerColumn({
							field: "reclamationId",
							headerName: i18n.t("number_shortened"),
							width: 65,
							mobile: "header",
						}),
						enumColumn({
							field: "reclamationState",
							headerName: i18n.t("state"),
							enumLabels: getReclamationStateLabels(),
							mobile: "cell",
						}),
						textColumn({
							field: "title",
							headerName: i18n.t("title"),
							width: 180,
							mobile: "header",
						}),
						textColumn({
							field: "subjectCode",
							headerName: i18n.t("subject_code"),
							width: 150,
							mobile: "cell",
						}),
						textColumn({
							field: "customerOrderLongName",
							headerName: i18n.t("customer_order"),
							width: 180,
							mobile: "cell",
						}),
						textColumn({
							field: "reclamationCategoryName",
							headerName: i18n.t("category"),
							mobile: "cell",
						}),
						textColumn({
							field: "responsiblePersonUserName",
							headerName: i18n.t("responsible_person"),
							width: 160,
						}),
						dateTimeColumn({
							field: "plannedCompletedDate",
							headerName: i18n.t("planned_completed_date_short"),
							width: 150,
							mobile: "cell",
						}),
						dateTimeColumn({
							field: "createdAt",
							headerName: i18n.t("created_at"),
							width: 150,
						}),
					]}
					openRow={(row) => {
						openDialog({
							title: i18n.t("reclamation"),
							size: "fullscreen",
							agentContext: {
								key: defineAgentUiContextKey("erp.reclamation.reclamation.reclamationsOfCustomerDataGrid.dialog"),
								pathSegments: [`${i18n.t("reclamation")} ${row.reclamationId}`],
								data: {
									customerId,
									reclamationId: row.reclamationId,
								},
							},
							content: <SingleReclamationView reclamationId={row.reclamationId} />,
						});
					}}
					listViewOnMobile
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerReclamations.grid"),
						data: {
							customerId,
						},
						selectedRowPathSegment: (row) => `${i18n.t("reclamation")} ${row.reclamationId}`,
						rowFields: ["reclamationId", "customerOrderId", "title"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
